import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../helpers/useAuth";
import { useOrganization } from "../helpers/useOrganization";
import { User } from "../helpers/User";
import { UserRoleArrayValues } from "../helpers/schema";
import { AuthErrorPage } from "./AuthErrorPage";
import { ShieldOff } from "lucide-react";
import { AuthLoadingState } from "./AuthLoadingState";
import styles from "./ProtectedRoute.module.css";

// Do not use this in pageLayout
const MakeProtectedRoute: (roles: User["role"][]) => React.FC<{
  children: React.ReactNode;
}> =
  (roles) =>
  ({ children }) => {
    const { authState } = useAuth();

    // Show loading state while checking authentication
    if (authState.type === "loading") {
      return <AuthLoadingState title="Authenticating" />;
    }

    // Redirect to login if not authenticated
    if (authState.type === "unauthenticated") {
      return <Navigate to="/login" replace />;
    }

    // Redirect to account recovery if the account is pending deletion
    if (authState.user.deletionPurgeAt) {
      return <Navigate to="/account-recovery" replace />;
    }

    if (!roles.includes(authState.user.role)) {
      return (
        <AuthErrorPage
          title="Access Denied"
          message={`Access denied. Your role (${authState.user.role}) lacks required permissions.`}
          icon={<ShieldOff className={styles.accessDeniedIcon} size={64} />}
        />
      );
    }

    // Render children if authenticated
    return <>{children}</>;
  };

// Create protected routes here, then import them in pageLayout

// Admin: full access to everything
export const AdminRoute = MakeProtectedRoute(["admin"]);

// Owner: can access owner, property manager, finance officer features
export const OwnerRoute = MakeProtectedRoute(["owner", "admin"]);

// Property Manager: can access property management, tenant, maintenance features
export const PropertyManagerRoute = MakeProtectedRoute([
  "property_manager",
  "owner",
  "admin",
]);

// Finance Officer: can access financial features
export const FinanceOfficerRoute = MakeProtectedRoute([
  "finance_officer",
  "owner",
  "admin",
]);

// Maintenance Staff: can access maintenance features
// Property managers and owners can also access maintenance features
export const MaintenanceStaffRoute = MakeProtectedRoute([
  "maintenance_staff",
  "property_manager",
  "owner",
  "admin",
]);

// Tenant: can access tenant portal features
// Property managers and owners can also access tenant features
export const TenantRoute = MakeProtectedRoute([
  "tenant",
  "property_manager",
  "owner",
  "admin",
]);

// User/Staff: base authenticated staff user (any non-tenant role)
// This allows access to the main dashboard and operational pages
export const UserRoute = MakeProtectedRoute([
  "admin",
  "owner",
  "property_manager",
  "finance_officer",
  "maintenance_staff",
  "user",
]);

// Alias for UserRoute for better semantic meaning
export const StaffRoute = UserRoute;

// Authenticated: for any authenticated user regardless of role
export const AuthenticatedRoute = MakeProtectedRoute(UserRoleArrayValues);

// Ensure user belongs to an organization
// If not, redirect to /onboarding
// Use this AFTER AuthenticatedRoute or other role-based routes
export const RequireOrganization: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { organization, isLoading } = useOrganization();

  // Show loading state while checking organization
  if (isLoading) {
    return <AuthLoadingState title="Checking Organization..." />;
  }

  // If no organization found, redirect to onboarding
  if (!organization) {
    return <Navigate to="/onboarding" replace />;
  }

  // Render children if organization exists
  return <>{children}</>;
};